import { z } from 'zod';
import { SOFTWARE_FAMILIES, type SoftwareFamily } from './game-profile.js';

/**
 * The version manager (docs/SOFTWARE.md): pick server software, a Minecraft version and a
 * build; hearthd installs it, setting the old launch files aside so the switch can be undone.
 */

export const softwareFamilySchema = z.enum(SOFTWARE_FAMILIES);

/** What a family's mods/plugins and worlds look like, for labels and warnings. */
export interface SoftwareFamilyInfo {
  label: string;
  /** Which add-ons it loads: Bukkit plugins, Fabric/Quilt mods, Forge/NeoForge mods, none. */
  ecosystem: 'plugins' | 'fabric' | 'forge' | 'none';
  /** Keeps the Nether and End in world_nether / world_the_end (Bukkit layout). */
  splitWorlds: boolean;
  description: string;
}

export const SOFTWARE_FAMILY_INFO: Record<SoftwareFamily, SoftwareFamilyInfo> = {
  paper: {
    label: 'Paper',
    ecosystem: 'plugins',
    splitWorlds: true,
    description: 'Fast, stable server with Bukkit/Paper plugins.',
  },
  purpur: {
    label: 'Purpur',
    ecosystem: 'plugins',
    splitWorlds: true,
    description: 'Paper with extra gameplay settings. Runs Paper plugins.',
  },
  folia: {
    label: 'Folia',
    ecosystem: 'plugins',
    splitWorlds: true,
    description: 'Paper split across CPU cores by region. Only Folia-ready plugins work.',
  },
  vanilla: {
    label: 'Vanilla',
    ecosystem: 'none',
    splitWorlds: false,
    description: 'Mojang’s own server. No plugins or mods.',
  },
  fabric: {
    label: 'Fabric',
    ecosystem: 'fabric',
    splitWorlds: false,
    description: 'Lightweight mod loader. Runs Fabric mods.',
  },
  quilt: {
    label: 'Quilt',
    ecosystem: 'fabric',
    splitWorlds: false,
    description: 'Fork of Fabric. Runs Quilt and most Fabric mods.',
  },
  forge: {
    label: 'Forge',
    ecosystem: 'forge',
    splitWorlds: false,
    description: 'The original mod loader. Runs Forge mods.',
  },
  neoforge: {
    label: 'NeoForge',
    ecosystem: 'forge',
    splitWorlds: false,
    description: 'Community fork of Forge. Runs NeoForge mods.',
  },
};

/** Minecraft and build ids: version-ish strings, never paths or options. */
export const softwareIdSchema = z.string().regex(/^[0-9A-Za-z][0-9A-Za-z.+_-]{0,63}$/);

export const minecraftVersionTypeSchema = z.enum(['release', 'pre-release', 'snapshot', 'old']);
export type MinecraftVersionType = z.infer<typeof minecraftVersionTypeSchema>;

/** Classify a Minecraft version id by its shape. */
export function minecraftVersionType(id: string): MinecraftVersionType {
  if (/^\d{2}w\d{2}[a-z]$|-snapshot-?\d+$/i.test(id)) return 'snapshot';
  if (/-(pre|rc)-?\d+$|\bpre-?release\b/i.test(id)) return 'pre-release';
  if (/^\d+\.\d+(\.\d+)?$/.test(id)) return 'release';
  return 'old';
}

/**
 * Compare two Minecraft versions: negative when a is older. Only plain and pre-release
 * versions compare (26.1 > 1.21.11; 1.21.4-rc1 < 1.21.4); null when either is a snapshot or
 * something else whose order can't be read from the name.
 */
export function compareMinecraftVersions(a: string, b: string): number | null {
  const parse = (v: string) => {
    const m = /^(\d+)\.(\d+)(?:\.(\d+))?(?:-(?:pre|rc)-?(\d+))?$/i.exec(v);
    if (!m) return null;
    // A pre-release sorts before its release: give releases an "infinite" pre number.
    return [Number(m[1]), Number(m[2]), Number(m[3] ?? 0), m[4] ? Number(m[4]) : Infinity];
  };
  const pa = parse(a);
  const pb = parse(b);
  if (!pa || !pb) return null;
  for (let i = 0; i < 4; i++) if (pa[i] !== pb[i]) return pa[i]! < pb[i]! ? -1 : 1;
  return 0;
}

export const softwareChannelSchema = z.enum(['recommended', 'stable', 'beta', 'experimental']);
export type SoftwareChannel = z.infer<typeof softwareChannelSchema>;

/** What is installed (or was, for undo), as recorded in .hearth/versions.json. */
export const softwareInstallSchema = z.object({
  /** Null when Hearth didn't install it (the egg did) and the family isn't known. */
  family: softwareFamilySchema.nullable(),
  minecraftVersion: z.string().nullable(),
  /** The build number, loader version or Forge/NeoForge version. */
  build: z.string().nullable(),
  /** How it starts: the jar variable's value, or unix_args.txt. */
  launch: z.object({ jar: z.string().nullable(), argsFile: z.boolean() }),
  /** Top-level files that belong to it (set aside on the next switch). */
  files: z.array(z.string()),
  /** False for what the egg installed before Hearth managed versions. */
  managed: z.boolean(),
  installedAt: z.string().nullable(),
});
export type SoftwareInstall = z.infer<typeof softwareInstallSchema>;

export const softwareJobSchema = z.object({
  id: z.string(),
  kind: z.enum(['switch', 'undo']),
  status: z.enum(['queued', 'running', 'succeeded', 'failed']),
  family: softwareFamilySchema.nullable(),
  minecraftVersion: z.string().nullable(),
  build: z.string().nullable(),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  error: z.object({ code: z.string(), message: z.string(), log: z.array(z.string()) }).nullable(),
  /** The server only picks the change up when it next starts. */
  restartRequired: z.boolean(),
  /** What it is doing now: the backup first (when asked for), then the install. */
  step: z.enum(['backup', 'install']).nullable().default(null),
  /** The server folder was (or is to be) wiped first; nothing can be undone. */
  wipe: z.boolean().default(false),
  /** The backup taken before the wipe, to restore from if anything goes wrong. */
  backup: z.object({ uuid: z.string(), name: z.string() }).nullable().default(null),
});
export type SoftwareJob = z.infer<typeof softwareJobSchema>;

export const softwareImageSchema = z.object({
  label: z.string(),
  image: z.string(),
  /** The Java major version read from the image name (java_21, :21), when it says. */
  java: z.number().int().nullable(),
});

export const softwareContextSchema = z.object({
  /** False when this server's game/egg has no version manager. */
  supported: z.boolean(),
  /** Families the profile offers on this egg, with why one can't be used here. */
  families: z.array(
    z.object({
      family: softwareFamilySchema,
      label: z.string(),
      description: z.string(),
      ecosystem: z.enum(['plugins', 'fabric', 'forge', 'none']),
    }),
  ),
  current: softwareInstallSchema.nullable(),
  /** What Undo would put back; null when there is nothing to go back to. */
  previous: softwareInstallSchema.nullable(),
  jarVariable: z.string().nullable(),
  /** The egg can start from unix_args.txt (needed by NeoForge and Forge 1.17–1.20.2). */
  argsFile: z.boolean(),
  image: z.string(),
  imageJava: z.number().int().nullable(),
  /** The egg's images, for switching Java with the software. */
  images: z.array(softwareImageSchema),
  /** The caller may also switch the image (startup.docker-image). */
  canChangeImage: z.boolean(),
  /** The caller may wipe the server with a switch (software.change and file.delete). */
  canWipe: z.boolean(),
  /** What the wipe confirmation must match exactly. */
  serverName: z.string(),
  /**
   * A backup can be taken before a wipe: the caller has backup.create and the server has a
   * free backup slot (no rotation, so taking it deletes nothing).
   */
  canBackup: z.boolean(),
  /** offline, starting, running, stopping, or unknown when the node didn't answer. */
  serverState: z.string(),
  job: softwareJobSchema.nullable(),
});
export type SoftwareContext = z.infer<typeof softwareContextSchema>;

export const softwareFamilyQuerySchema = z.object({ family: softwareFamilySchema });

export const softwareVersionSchema = z.object({
  id: z.string(),
  type: minecraftVersionTypeSchema,
  /** Why it can't be installed on this server, when it can't. */
  unavailable: z.string().nullable(),
});
export const softwareVersionsSchema = z.object({
  family: softwareFamilySchema,
  /** Newest first. */
  versions: z.array(softwareVersionSchema),
  /** The newest release that can be installed. */
  latest: z.string().nullable(),
});
export type SoftwareVersions = z.infer<typeof softwareVersionsSchema>;

export const softwareBuildsQuerySchema = z.object({
  family: softwareFamilySchema,
  version: softwareIdSchema,
});

export const softwareBuildSchema = z.object({
  id: z.string(),
  label: z.string(),
  channel: softwareChannelSchema,
  publishedAt: z.string().nullable(),
  unavailable: z.string().nullable(),
});
export const softwareBuildsSchema = z.object({
  family: softwareFamilySchema,
  minecraftVersion: z.string(),
  /** Newest first. */
  builds: z.array(softwareBuildSchema),
  /** The build Hearth picks by default: the project's recommended one, else the newest stable. */
  recommended: z.string().nullable(),
  latest: z.string().nullable(),
});
export type SoftwareBuilds = z.infer<typeof softwareBuildsSchema>;

export const softwareTargetSchema = z.object({
  family: softwareFamilySchema,
  minecraftVersion: softwareIdSchema,
  build: softwareIdSchema,
});

export const softwareWarningSchema = z.object({
  code: z.enum([
    'downgrade',
    'ecosystem_change',
    'world_layout',
    'folia',
    'unstable_version',
    'unstable_build',
    'unmanaged_current',
  ]),
  message: z.string(),
});
export type SoftwareWarning = z.infer<typeof softwareWarningSchema>;

export const softwarePreviewSchema = z.object({
  family: softwareFamilySchema,
  minecraftVersion: z.string(),
  build: z.string(),
  buildLabel: z.string(),
  warnings: z.array(softwareWarningSchema),
  /** The Java the version needs, when known, and an image of the egg that has it. */
  javaRequired: z.number().int().nullable(),
  javaOk: z.boolean(),
  suggestedImage: z.string().nullable(),
  /** Startup variables the switch sets. */
  variables: z.array(z.object({ name: z.string(), from: z.string(), to: z.string() })),
  launch: z.enum(['jar', 'args_file']),
});
export type SoftwarePreview = z.infer<typeof softwarePreviewSchema>;

export const softwareSwitchBodySchema = softwareTargetSchema.extend({
  /** Also switch to this image of the egg (needs startup.docker-image). */
  image: z.string().max(255).optional(),
  /** Required when the preview had warnings. */
  acceptWarnings: z.boolean().default(false),
  /**
   * Delete everything in the server folder before installing (needs file.delete too).
   * `confirm` must be the server's name, exactly.
   */
  wipe: z.object({ confirm: z.string().max(191) }).optional(),
  /** With a wipe: take a backup first and wait for it; a failed backup stops the switch. */
  backup: z.boolean().default(false),
});

/**
 * Warnings for switching from `from` to `to`. Pure, so the web shows exactly what the API
 * checks. `from` is null when nothing is known about the current software.
 */
export function softwareWarnings(
  from: Pick<SoftwareInstall, 'family' | 'minecraftVersion' | 'managed'> | null,
  to: { family: SoftwareFamily; minecraftVersion: string; channel: SoftwareChannel },
): SoftwareWarning[] {
  const out: SoftwareWarning[] = [];
  const toInfo = SOFTWARE_FAMILY_INFO[to.family];
  if (!from || (!from.managed && !from.family))
    out.push({
      code: 'unmanaged_current',
      message:
        'Hearth doesn’t know which software this server runs now. Make a backup before switching.',
    });
  const fromInfo = from?.family ? SOFTWARE_FAMILY_INFO[from.family] : null;
  if (from?.minecraftVersion) {
    const cmp = compareMinecraftVersions(to.minecraftVersion, from.minecraftVersion);
    if (cmp !== null && cmp < 0)
      out.push({
        code: 'downgrade',
        message: `Minecraft ${to.minecraftVersion} is older than ${from.minecraftVersion}. Worlds saved by a newer version can fail to load or lose blocks and items. Make a backup first.`,
      });
  }
  if (fromInfo && from?.family !== to.family && fromInfo.ecosystem !== toInfo.ecosystem) {
    const what = (e: SoftwareFamilyInfo['ecosystem']) =>
      e === 'plugins'
        ? 'plugins'
        : e === 'fabric'
          ? 'Fabric/Quilt mods'
          : e === 'forge'
            ? 'Forge/NeoForge mods'
            : 'no add-ons';
    out.push({
      code: 'ecosystem_change',
      message: `${fromInfo.label} runs ${what(fromInfo.ecosystem)}; ${toInfo.label} runs ${what(toInfo.ecosystem)}. Installed ${fromInfo.ecosystem === 'plugins' ? 'plugins' : 'mods'} won’t load, and blocks or items they added can disappear from the world.`,
    });
  }
  if (fromInfo?.splitWorlds && !toInfo.splitWorlds)
    out.push({
      code: 'world_layout',
      message: `${fromInfo.label} keeps the Nether and the End in world_nether and world_the_end. ${toInfo.label} reads them from inside the world folder, so it will start them fresh unless you move them.`,
    });
  if (to.family === 'folia' && from?.family !== 'folia')
    out.push({
      code: 'folia',
      message: 'Folia runs regions on separate threads. Most plugins don’t support it yet.',
    });
  const type = minecraftVersionType(to.minecraftVersion);
  if (type !== 'release')
    out.push({
      code: 'unstable_version',
      message: `Minecraft ${to.minecraftVersion} is a ${type === 'old' ? 'very old or unusual' : type} version.`,
    });
  if (to.channel === 'beta' || to.channel === 'experimental')
    out.push({
      code: 'unstable_build',
      message: `This build is marked ${to.channel}; it may be unstable.`,
    });
  return out;
}

/** The Java major version an image name says it has (yolks:java_21, eclipse-temurin:21-jre). */
export function imageJavaVersion(image: string): number | null {
  const m = /java[_-]?(\d{1,2})\b/i.exec(image) ?? /:(\d{1,2})(?:[.-]|$)/.exec(image);
  const n = m ? Number(m[1]) : 0;
  // Java 8 is the oldest any Minecraft server uses; a smaller number is some other tag.
  return n >= 8 ? n : null;
}
